/**
 * Embedded Asset Registry
 *
 * Provides a unified way to resolve embedded engine assets (textures, sounds, etc.)
 * that are bundled with engine packages. Works transparently in both dev and prod modes.
 *
 * Dev mode (Vite): Assets served from /packages/{pkg}/src/{path}
 * Prod mode (packaged): Assets copied to ./{pkg}/{filename} by CopyRuntimeDepsStep
 *
 * @example
 * ```ts
 * import { embeddedAssets } from '@engine/core';
 *
 * // Register an asset (typically at module load time)
 * embeddedAssets.register('aqua-noise', 'render2d', 'textures/noise-granulation.png');
 *
 * // Resolve URL at runtime
 * const url = embeddedAssets.resolve('aqua-noise');
 * const response = await fetch(url);
 * ```
 *
 * @module @engine/core/assets
 */

export interface EmbeddedAssetEntry {
  /** Unique asset identifier */
  id: string;
  /** Package name (e.g., 'render2d', 'vfx') */
  packageName: string;
  /** Path relative to package src directory */
  srcPath: string;
  /** Optional: override filename in prod (useful to avoid collisions) */
  prodFilename?: string;
}

/**
 * Runtime mode detection and asset URL resolution
 */
class EmbeddedAssetRegistry {
  private entries = new Map<string, EmbeddedAssetEntry>();
  private _isDev: boolean | null = null;

  /**
   * Check if running in dev mode (Vite dev server)
   */
  get isDev(): boolean {
    if (this._isDev === null) {
      this._isDev = this.detectDevMode();
    }
    return this._isDev;
  }

  /**
   * Force mode (useful for testing or special builds)
   */
  setMode(isDev: boolean): void {
    this._isDev = isDev;
  }

  private detectDevMode(): boolean {
    // Check location protocol
    if (typeof location !== 'undefined') {
      const proto = location.protocol;
      // Vite dev server uses http/https
      if (proto === 'http:' || proto === 'https:') {
        return true;
      }
    }
    // file:// protocol = packaged build
    return false;
  }

  /**
   * Register an embedded asset.
   *
   * @param id - Unique identifier for the asset
   * @param packageName - Package name without @engine/ prefix
   * @param srcPath - Path relative to package src directory
   * @param prodFilename - Optional override filename in prod (to avoid collisions)
   */
  register(
    id: string,
    packageName: string,
    srcPath: string,
    prodFilename?: string,
  ): void {
    if (this.entries.has(id)) {
      console.warn(`[EmbeddedAssets] Asset '${id}' already registered, overwriting`);
    }
    this.entries.set(id, {
      id,
      packageName,
      srcPath,
      prodFilename,
    });
  }

  /**
   * Resolve asset URL by ID.
   *
   * @param id - Registered asset ID
   * @returns URL string suitable for fetch()
   * @throws If asset is not registered
   */
  resolve(id: string): string {
    const entry = this.entries.get(id);
    if (!entry) {
      throw new Error(`[EmbeddedAssets] Unknown asset: '${id}'. Did you forget to register it?`);
    }
    return this.resolveEntry(entry);
  }

  /**
   * Resolve asset URL directly without registration.
   * Useful for one-off resolutions.
   *
   * @param packageName - Package name (e.g., 'render2d')
   * @param srcPath - Path relative to package src (e.g., 'textures/noise.png')
   * @param prodFilename - Optional override filename in prod
   */
  resolveUnregistered(packageName: string, srcPath: string, prodFilename?: string): string {
    return this.resolveEntry({
      id: '',
      packageName,
      srcPath,
      prodFilename,
    });
  }

  private resolveEntry(entry: EmbeddedAssetEntry): string {
    if (this.isDev) {
      // Dev mode: Vite serves packages from workspace root
      return `/packages/${entry.packageName}/src/${entry.srcPath}`;
    }

    // Prod mode: assets copied to assets/ directory
    const filename = entry.prodFilename ?? entry.srcPath.split('/').pop()!;
    return `./assets/${filename}`;
  }

  /**
   * Get all registered assets (useful for build tools)
   */
  getAll(): ReadonlyMap<string, EmbeddedAssetEntry> {
    return this.entries;
  }

/**
 * Get asset registration info for build tools.
 * Returns info needed to copy assets during game export.
 */
getBuildInfo(): Array<{
  id: string;
  packageName: string;
  srcPath: string;
  outputPath: string;
}> {
  const result: Array<{
    id: string;
    packageName: string;
    srcPath: string;
    outputPath: string;
  }> = [];

  for (const entry of this.entries.values()) {
    const filename = entry.prodFilename ?? entry.srcPath.split('/').pop()!;
    result.push({
      id: entry.id,
      packageName: entry.packageName,
      srcPath: entry.srcPath,
      outputPath: `assets/${filename}`,
    });
  }

  return result;
}

  /**
   * Check if an asset is registered
   */
  has(id: string): boolean {
    return this.entries.has(id);
  }
}

/**
 * Global embedded asset registry instance
 */
export const embeddedAssets = new EmbeddedAssetRegistry();

// ─── Built-in asset registrations (driven by /embedded-assets.json) ────────
import manifest from '../../../../embedded-assets.json';
for (const entry of manifest) {
  embeddedAssets.register(entry.id, entry.package, entry.src);
}
